/**
 * Created by Ning Jiang on 2021-05-07.
 */

import {KarenLayers, Layers} from "../view/KarenLayers";
import {KarenSymbol} from "../symbol/KarenSymbol";
import {ReelState} from "../../core/reel/ReelState";
import {Bounce, gsap, TimelineLite, TweenLite} from "gsap";
import {Karen} from "../Karen";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {KarenGameConfig} from "../KarenGameConfig";
import {KarenSymbolState} from "../symbol/KarenSymbolConfig";

export interface IReelPosH {
    [reelSize: number]: number
}
export interface IMaskY {
    scaleY:number,
    positionY:number
}

export class KarenReelView {

    // TODO: Change the width wider to make the mask wider for wide symbol animations.
    public static REEL_AREAS: PIXI.Rectangle[] = [
        new PIXI.Rectangle(60, 264 - 132, ( KarenGameConfig.SYM_WIDTH ), ( KarenGameConfig.SYM_HEIGHT[0] ) * 2),
        new PIXI.Rectangle(181, 264 - 48, ( KarenGameConfig.SYM_WIDTH ), ( KarenGameConfig.SYM_HEIGHT[1] ) * 3),
        new PIXI.Rectangle(301, 216, ( KarenGameConfig.SYM_WIDTH ), ( KarenGameConfig.SYM_HEIGHT[2] ) * 3),
        new PIXI.Rectangle(421, 216, ( KarenGameConfig.SYM_WIDTH ), ( KarenGameConfig.SYM_HEIGHT[3] ) * 3),
        new PIXI.Rectangle(542, 216, ( KarenGameConfig.SYM_WIDTH ), ( KarenGameConfig.SYM_HEIGHT[4] ) * 3),
        new PIXI.Rectangle(662, 216, ( KarenGameConfig.SYM_WIDTH ), ( KarenGameConfig.SYM_HEIGHT[5] ) * 3),
    ];
    public static reelPosHeight: IReelPosH = {3: 216, 4: 174, 5: 132, 6: 90, 7: 48};
    public isSlowAttentionPlaying: boolean = false;
    public playWildInNormalState: boolean = false;
    public reelId: number;
    public allSymbols: KarenSymbol[] = [];
    public wantBounce: boolean = false;
    public spinStepOffset: number = 0;
    public nextSpinStepConfig: {
        reelState?: ReelState,
        duration?: number,
        ease?: string
    } | null;
    private readonly _lowContainer: PIXI.Container;
    // TODO: have more layers for sticky symbols and so on.
    private readonly _wildContainer: PIXI.Container;
    private readonly _lowWinContainer: PIXI.Container;
    private _lastStepOffset: number;
    private _symbolsNumTop: number;
    private _stepDuration: number;
    private _stepEase: string = "none";
    private _currentTl: TimelineLite;
    private _positionsSnapshot: number[];
    private _defaultSymbolsSized: number = 3;
    private _isPositionSnapshotDirty: boolean = true;

    constructor(reelId: number) {
        this.reelId = reelId;
        this._lowContainer = this.createSymbolContainer(reelId);
        KarenLayers.getLayer(Layers.LOW_SYMBOLS)?.addChild(this._lowContainer);
        this._lowWinContainer = this.createSymbolContainer(reelId);
        KarenLayers.getLayer(Layers.LOW_SYMBOLS_WIN)?.addChild(this._lowWinContainer);
        if (this.reelId === 5) {
            this._wildContainer = this.createSymbolContainer(reelId);
            KarenLayers.getLayer(Layers.WILD_CONTAINER)?.addChild(this._wildContainer);
        }
    }

    public get stepDistance(): number {
        return this.allSymbols[this._symbolsNumTop].symbolStep;
    }

    public get currentStepLeftTime(): number {
        if (this._currentTl != null && this._currentTl.isActive()) {
            return this._currentTl.duration() * ( 1 - this._currentTl.progress() );
        }
        return 0;
    }

    public get currentStepDuration(): number {
        if (this._currentTl) {
            return this._currentTl.duration();
        }
        return 0;
    }

    public addSymbol(symbol: KarenSymbol): void {
        if (symbol!.symbolName === "W") {
            this._wildContainer.addChild(symbol);
        } else {
            this._lowContainer.addChild(symbol);
        }
    }

    public addWinSymbol(symbol: KarenSymbol): void {
        if (symbol!.symbolName === "W") {
            this._wildContainer.addChild(symbol);
        } else {
            this._lowWinContainer.addChild(symbol);
        }
    }

    public removeSymbol(symbol: KarenSymbol): void {
        if (symbol.parent) {
            symbol.parent.removeChild(symbol);
        }
    }

    public initSymbolsPosition(symbolsNumTop: number): void {
        this._symbolsNumTop = symbolsNumTop;
        let offset: number = 0;
        for (let symIndex: number = symbolsNumTop; symIndex < this.allSymbols.length; symIndex++) {
            this.allSymbols[symIndex].y = offset + this.allSymbols[symIndex].symbolHeight * 0.5;
            offset += this.allSymbols[symIndex].symbolStep;
        }
        if (symbolsNumTop > 0) {
            offset = 0;
            for (let symIndex: number = symbolsNumTop - 1; symIndex >= 0; symIndex--) {
                this.allSymbols[symIndex].y = offset - this.allSymbols[symIndex].bottomPadding - this.allSymbols[symIndex].symbolHeight * 0.5;
                offset -= this.allSymbols[symIndex].symbolStep;
            }
        }
    }

    public step(newSymbol: KarenSymbol, removeSymbolCallback: () => void, stepDistance?: number,): TimelineLite {
        if (this.isSlowAttentionPlaying) {
            this.nextSpinStepConfig = {
                duration: 0.6,
            };
        }
        if (this.playWildInNormalState) {
            if (newSymbol.symbolName === "W") {
                newSymbol.getStateAnimation(KarenSymbolState.SPIN,
                    {duration: 0.01, alpha: 0},
                    {duration: 0.01, alpha: 0});
                newSymbol!.getStateAnimation(
                    KarenSymbolState.NORMAL,
                    {duration: 0.1, alpha: 1},
                    {duration: 0.1, alpha: 0});
            }
        }
        const reelState: ReelState | undefined = this.nextSpinStepConfig?.reelState;
        const stepDuration: number = this.nextSpinStepConfig?.duration ? this.nextSpinStepConfig.duration : this._stepDuration;
        const stepEase: string = this.nextSpinStepConfig?.ease ? this.nextSpinStepConfig.ease : this._stepEase;
        if (stepDistance == null) {
            stepDistance = this.stepDistance; // stepDistance is the size of first coming symbol.
        }
        this.nextSpinStepConfig = null;

        this._currentTl = gsap.timeline();
        this._currentTl.add(() => {
            if (reelState) {
                Karen.reels[this.reelId].reelState = reelState;
            }
            this._stepDuration = stepDuration;
            this._stepEase = stepEase;
            this.spinStepOffset = 0;
            this.updatePositionSnapshot();
        });
        this._currentTl.add(gsap.to(this,
            {
                duration: stepDuration,
                ease: stepEase,
                spinStepOffset: stepDistance,
                onUpdate: () => {
                    this.allSymbols.forEach((symbol, index) => {
                        symbol.y = this._positionsSnapshot[index] + this.spinStepOffset;
                    });
                    if (this.wantBounce && this.spinStepOffset < this._lastStepOffset) {
                        this.doBounce();
                        return;
                    }
                    this._lastStepOffset = this.spinStepOffset;
                }
            }));
        this._currentTl.add(() => {
            this._isPositionSnapshotDirty = true;
            removeSymbolCallback();
            if (this.wantBounce) {
                this.doBounce();
            }
        });

        return this._currentTl;
    }

    public nudgeStep(dataIndex: number, abort: boolean, callback: () => void): TimelineLite {
        const reelState: ReelState | undefined = this.nextSpinStepConfig?.reelState;
        const stepDuration: number = abort ? 0 : this._stepDuration;
        const stepEase: string = this.nextSpinStepConfig?.ease ? this.nextSpinStepConfig.ease : this._stepEase;
        this._currentTl = gsap.timeline();
        this._currentTl.add(() => {
            if (reelState) {
                Karen.reels[this.reelId].reelState = reelState;
            }
            this._stepDuration = stepDuration;
            this._stepEase = stepEase;
            this.spinStepOffset = 0;
            this.updatePositionSnapshot();
            this.swapArrayElements(this.allSymbols, dataIndex, dataIndex + 1);
        });
        this._currentTl.add(this.moveStepUp(dataIndex, stepDuration));
        this._currentTl.add(() => {
            this._isPositionSnapshotDirty = true;
            callback();
            if (this.wantBounce) {
                this.doBounce();
            }
        });
        return this._currentTl;
    }

    public increaseReelMask(reelId: number,notAnimate:boolean=false): TimelineLite {
        let reelSize = Karen.model.parsedGameData.evaluatedArea[reelId].length;
        let tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            if(this._defaultSymbolsSized<7){
                this._defaultSymbolsSized++;
            }
            if(reelSize<this._defaultSymbolsSized){
                reelSize =this._defaultSymbolsSized;
            }
            if(notAnimate){
                this.updateReelMaskWithoutAnimation(reelId, this._defaultSymbolsSized);
            }else {
                this.updateReelMask(reelId, this._defaultSymbolsSized);
            }
        });
        return tl;
    }
    private calculateScaleAndPosition(reelId:number,reelSize?:number):IMaskY{
        if(!reelSize) {
            reelSize = Karen.model.parsedGameData.evaluatedArea[reelId].length;
        }
        let originY: number = 216;
        const defaultSymbolHeight: number = 252;
        this._defaultSymbolsSized = reelSize;
        let scaleY: number = 1;
        let positionY: number = KarenReelView.reelPosHeight[reelSize];
        if (reelSize > 3) {
            scaleY = ( ( KarenGameConfig.SYM_HEIGHT[reelId] ) * reelSize ) / defaultSymbolHeight;
            if (reelId !== 0) {
                positionY = KarenReelView.reelPosHeight[reelSize];
            }
        } else {
            scaleY = 1;
            positionY = originY;
        }
        if (reelId === 0) {
            positionY = KarenReelView.REEL_AREAS[reelId].y;
        }
        return {
            scaleY:scaleY,
            positionY:positionY
        }
    }
    public updateReelMask(reelId: number, reelSize: number, tobeSkipped: boolean = false): TimelineLite {
        if (reelId == 5) {
            this.updateWildContainer(reelSize);
        }
        let tl: TimelineLite = new TimelineLite();
        const mask: PIXI.Graphics = this._lowContainer.getChildByName("reelMask") as PIXI.Graphics;
        const winMask: PIXI.Graphics = this._lowWinContainer.getChildByName("reelMask") as PIXI.Graphics;
        let scaleObject:IMaskY = this.calculateScaleAndPosition(reelId,reelSize);
        let scaleY:number = scaleObject.scaleY;
        let positionY:number = scaleObject.positionY;
        let tl1 = TweenLite.to([winMask.scale, mask.scale], 0.2, {y: scaleY, ease: Bounce.easeOut});
        let tl2 = TweenLite.to([this._lowWinContainer, this._lowContainer], 0.2, {y: positionY, ease: Bounce.easeOut});
        tl.add([tl1, tl2]);
        if (tobeSkipped) {
            tl.progress(1);
        }
        return tl;
    }
    public updateReelMaskWithoutAnimation(reelId: number, reelSize: number, tobeSkipped: boolean = false): TimelineLite {
        if (reelId == 5) {
            this.updateWildContainer(reelSize);
        }
        let tl: TimelineLite = new TimelineLite();
        const mask: PIXI.Graphics = this._lowContainer.getChildByName("reelMask") as PIXI.Graphics;
        const winMask: PIXI.Graphics = this._lowWinContainer.getChildByName("reelMask") as PIXI.Graphics;
        let scaleObject:IMaskY = this.calculateScaleAndPosition(reelId,reelSize);
        let scaleY:number = scaleObject.scaleY;
        let positionY:number = scaleObject.positionY;
        winMask.scale.y = scaleY;
        mask.scale.y = scaleY;
        this._lowWinContainer.y =positionY;
        this._lowContainer.y =positionY;
        return tl;
    }

    //#106 to move wild to layer above  reelFrame
    public updateWildContainer(reelSize: number, reset: boolean = false, showTopB: boolean = false): void {
        let originY: number = 216;
        const defaultSymbolHeight: number = 252;
        let scaleY: number = 1;
        let positionY: number = KarenReelView.reelPosHeight[reelSize];
        this._wildContainer.position.set(KarenReelView.REEL_AREAS[5].x, KarenReelView.REEL_AREAS[5].y);
        const hmask: PIXI.Graphics = this._wildContainer.getChildByName("reelMask") as PIXI.Graphics;
        if (reset) {
            scaleY = 1;
            hmask.y = 0;
            positionY = originY;
        } else {
            scaleY = ( ( ( KarenGameConfig.SYM_HEIGHT[5] ) * reelSize ) / defaultSymbolHeight );
            hmask.y = 0;
            positionY = KarenReelView.reelPosHeight[reelSize];
            if (showTopB) {
                scaleY = ( ( ( KarenGameConfig.SYM_HEIGHT[5] ) * reelSize ) / defaultSymbolHeight ) + 0.3;
                hmask.y = -30;
            }
        }
        hmask.scale.y = scaleY;
        this._wildContainer.y = positionY;
        this._wildContainer.addChild(hmask);
        this._wildContainer.mask = hmask;
    }

    //#141 #148
    public setInteractiveSymbols(bool: boolean) {
        this.allSymbols.forEach((sym: KarenSymbol) => {
            sym.setInteractive(bool);
        })
    }

    private moveStepUp(dataIndex: number, stepDuration: number): TimelineLite {
        const effectedSym: KarenSymbol = this.allSymbols[dataIndex + 1];
        const wildSym: KarenSymbol = this.allSymbols[dataIndex];
        const wildSymPos: number = wildSym.y;
        const effectedNudgeSymPos: number = effectedSym.y;
        const tl: TimelineLite = new TimelineLite({});
        const posY: number = wildSym.getMutliper().y;
        tl.add(gsap.to(wildSym, stepDuration, {y: effectedNudgeSymPos, ease: this._stepEase}), 0);
        tl.add(gsap.to(wildSym.getMutliper(), stepDuration, {y: posY + 84, ease: this._stepEase}), 0);
        tl.add(gsap.to(effectedSym, stepDuration, {y: wildSymPos, ease: this._stepEase}), 0);
        return tl;
    }

    private swapArrayElements(arr: KarenSymbol[], indexA: number, indexB: number): void {
        const temp: KarenSymbol = arr[indexA];
        arr[indexA] = arr[indexB];
        arr[indexB] = temp;
    };

    private createSymbolContainer(reelId: number): PIXI.Container {
        const mask: PIXI.Graphics = new PIXI.Graphics();
        mask.beginFill(0xFF0000, 0.2);
        mask.drawShape(new PIXI.Rectangle(-( KarenReelView.REEL_AREAS[reelId].width ) * 0.5, 0, KarenReelView.REEL_AREAS[reelId].width, KarenReelView.REEL_AREAS[reelId].height));
        mask.endFill();
        mask.name = "reelMask";
        const container = new PIXI.Container();
        container.position.set(KarenReelView.REEL_AREAS[reelId].x, KarenReelView.REEL_AREAS[reelId].y);
        container.addChild(mask);
        container.mask = mask;
        return container;
    }

    private updatePositionSnapshot(): void {
        // To snapshot the symbols start posion for each step. it's not sure if the onUpdate or onStart triggered first, so need to trigger this one in both places.
        if (this._isPositionSnapshotDirty) {
            this._positionsSnapshot = ArrayHelper.initArrayWithValues(this.allSymbols.length, index => this.allSymbols[index].y);
            this._isPositionSnapshotDirty = false;
            this._lastStepOffset = 0;
        }
    }

    private doBounce(): void {
        this.wantBounce = false;
        Karen.reels[this.reelId].reelState = ReelState.BOUNCE;
    }
}
